import { Risk, RiskMitigation, GroupId } from "components/Risk/types";

export enum AIHazardStatus {
  suggested = "suggested",
  generated = "generated",
  accepted = "accepted",
  dismissed = "dismissed",
}

export type AIHazardSeverity = "high" | "medium" | "low";

// Mitigations in AI drafts MUST carry reasoning (idea.md methodology).
export type AIDraftMitigation = RiskMitigation & { reasoning: string };

// Existing Risk shape minus server-minted/blueprint fields, plus origin_id
// (known typing gap: form field not declared on Risk) and reasoning-required mitigations.
export type AIDraftRisk = Omit<
  Risk,
  | "id"
  | "resource_id"
  | "created_at"
  | "last_updated"
  | "is_blueprint"
  | "blueprint_name"
  | "blueprint_description"
  | "mitigations"
> & { origin_id: string; mitigations: AIDraftMitigation[] };
// Contract rules: fully_mitigated always false in AI output; mitigation id minted by
// backend per draft (mock: "mock-mit-<n>"), version: 1; applied_standards ⊆ project.standards.
// consequence_id / phase_id wire format: SEMICOLON-joined value strings (pinned from
// src/components/inputs/MultiSelectInput.tsx — see api_contracts.md "Wire format").

export interface AIMethodologyWarning {
  code: string;
  message: string;
  mitigation_index?: number;
}

export interface AISkippedCategory {
  category: "INH" | "EMS" | "ESS" | "PIK" | "BDA" | "PSA";
  reason: string; // idea.md: "if a category has no measures, state why"
}

export interface AIHazardShell {
  id: string; // backend-minted, opaque (assumption 9)
  name: string;
  description: string;
  reasoning: string; // why the AI suggested this hazard (stage 1)
  severity: AIHazardSeverity; // stage-1 triage badge only; never lands on Risk
  group_id_hint: GroupId; // G1 hazard-category hint from stage 1 (GroupTypes name)
  status: AIHazardStatus;
  user_context?: string; // per-hazard extra info for stage 2
  draft?: AIDraftRisk | null; // persisted working copy (status generated+)
  pending_proposal?: AIDraftRisk | null; // persisted regeneration proposal awaiting diff resolution
  generation_warnings: AIMethodologyWarning[]; // latest generation, persisted
  skipped_categories: AISkippedCategory[]; // latest generation, persisted
  accepted_risk_resource_id?: string; // provenance + idempotency, set on accept
  reviewed_at?: string; // acknowledgment timestamp, set on accept
  created_at: string;
  updated_at: string;
}

export interface AIHazardGroup {
  // physical component/subsystem (NOT GroupTypes)
  id: string;
  label: string; // → RiskGroupping.label on first accept
  description: string;
  hazard_ids: string[];
}

export interface AIRiskSession {
  id: string;
  project_crn: string;
  additional_context: string; // project-level user context (stage-1 input)
  language: string;
  groups: AIHazardGroup[];
  hazards: AIHazardShell[]; // flat; groups reference by id
  last_analyzed_at: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Request/response types for the api_contracts.md endpoints (plan §3)
// ---------------------------------------------------------------------------

export type AIAnalyzeMode = "initial" | "reanalyze";

// POST /projects/:projectId/ai-risk-assessment/analyze
export interface AIAnalyzeRequest {
  language: string;
  additional_context: string;
  mode: AIAnalyzeMode;
}

export type AIGenerateMode = "initial" | "regenerate";

// POST /projects/:projectId/ai-risk-assessment/hazards/:hazardId/generate
export interface AIGenerateRequest {
  language: string;
  mode: AIGenerateMode;
  // Freshest shell values sent so unsaved edits are honored;
  // the server persists them onto the shell.
  name: string;
  description: string;
  user_context: string;
  regeneration_context?: string;
}

// PATCH /projects/:projectId/ai-risk-assessment/hazards/:hazardId — partial body.
export interface AIHazardPatch {
  name?: string;
  description?: string;
  user_context?: string;
  status?: AIHazardStatus; // dismiss/restore transitions only
  draft?: AIDraftRisk; // full draft — Phase-B saves & diff merges
  pending_proposal?: AIDraftRisk | null; // null to discard/resolve a proposal
}

// POST /projects/:projectId/ai-risk-assessment/hazards/:hazardId/accept
export interface AIAcceptRequest {
  risk: AIDraftRisk; // final user-edited draft
  fully_mitigated: boolean; // user-decided in the Accept Gate (AI value is always false)
  acknowledged: true;
  group_label: string;
}

export interface AIAcceptResponse {
  risk: Risk;
  risk_grouping_id: string;
  session: AIRiskSession;
}

export type AIApiErrorCode =
  | "no_session"
  | "insufficient_context"
  | "invalid_state"
  | "generation_failed"
  | "vocabulary_violation"
  | "model_unavailable";

export interface AIApiErrorBody {
  error: { code: AIApiErrorCode; message: string };
}

// ---------------------------------------------------------------------------
// Client-side validation (plan §8) & diff (plan §7) types
// ---------------------------------------------------------------------------

export interface DraftIssue {
  code: string;
  i18nKey: string;
  params?: Record<string, unknown>;
  section: "risk" | `mitigation_${number}`;
}

export interface DraftValidation {
  errors: DraftIssue[];
  warnings: DraftIssue[];
}

export type DiffDecision = "keep" | "take";

export interface RiskDiffSection {
  key: string; // "basics" | "classification" | "assessment" | "reasoning" | "mitigation:<i>"
  kind:
    | "basics"
    | "classification"
    | "assessment"
    | "reasoning"
    | "mitigation"
    | "mitigation_added"
    | "mitigation_removed";
  mitigationIndex?: number;
  changed: boolean;
  current: unknown | null;
  proposed: unknown | null;
}
